import { useState, useMemo } from "react";
import { X, Search, Check, Trash2, Folder, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function flattenTreeItems(node) {
  return [...node.items, ...node.children.flatMap(flattenTreeItems)];
}

function ItemTile({ item, selected, onToggle, onDeleteItem }) {
  return (
    <div
      onClick={() => onToggle(item.id)}
      className={`group relative flex flex-col items-center gap-1.5 p-2.5 rounded-xl border cursor-pointer transition-all ${
        selected ? "bg-blue-50 border-blue-400 shadow-sm" : "bg-slate-50 border-slate-200 hover:border-blue-300 hover:bg-blue-50"
      }`}
    >
      {selected && (
        <span className="absolute top-1.5 right-1.5 flex items-center justify-center h-4 w-4 rounded-full bg-blue-500 text-white">
          <Check className="h-2.5 w-2.5" />
        </span>
      )}
      {onDeleteItem && (
        <button
          type="button"
          title="Удалить из каталога"
          onClick={e => { e.stopPropagation(); onDeleteItem(item.id); }}
          className="absolute top-1.5 left-1.5 h-5 w-5 flex items-center justify-center rounded-full bg-white/90 border border-slate-200 text-slate-400 opacity-0 group-hover:opacity-100 hover:text-red-600 hover:border-red-200 transition-opacity"
        >
          <Trash2 className="h-2.5 w-2.5" />
        </button>
      )}
      {item.thumb}
      <span className="text-[11px] font-medium text-center leading-tight text-slate-700 line-clamp-2 w-full">{item.name}</span>
      {item.meta && <span className="text-[10px] text-slate-400">{item.meta}</span>}
    </div>
  );
}

// Generic "pick multiple items from a catalog" modal — used for products/insoles and exercises
// on the Scanner page, and for purchases on the patient card.
//
// Two modes:
// - `groups={[{key,label,items}]}` — flat sections (exercises have no group hierarchy at all).
// - `groupTree={{id,name,children,items}}` — drill-down folder navigation (products/insoles,
//   which have up to 3 levels of nesting). Search bypasses folders and flattens the whole tree.
export default function CatalogPickerModal({ title, groups, groupTree, selectedIds, onToggle, onClose, onDeleteItem }) {
  const [query, setQuery] = useState("");
  const [path, setPath] = useState([]); // array of visited tree nodes, root implicit at []

  const filteredGroups = useMemo(() => {
    if (!groups) return null;
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.map(g => ({ ...g, items: g.items.filter(i => i.name.toLowerCase().includes(q)) }));
  }, [groups, query]);

  const currentNode = groupTree ? (path.length ? path[path.length - 1] : groupTree) : null;
  const searchResults = useMemo(() => {
    if (!groupTree) return null;
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return flattenTreeItems(groupTree).filter(i => i.name.toLowerCase().includes(q));
  }, [groupTree, query]);

  // Только для плоского режима (упражнения) — у groupTree своя проверка пустоты ПО ТЕКУЩЕЙ
  // открытой папке (currentNode) ниже, не по корню целиком.
  const isEmpty = groups && groups.every(g => g.items.length === 0);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4"
      onMouseDown={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col" style={{ maxWidth: 640, maxHeight: "85vh" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <span className="text-base font-bold text-slate-900">{title}</span>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="px-5 pt-4 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input className="pl-8 h-8 text-xs" placeholder="Поиск..." value={query} onChange={e => setQuery(e.target.value)} autoFocus />
          </div>
        </div>

        {groupTree && !query && (
          <div className="flex items-center gap-1 px-5 pt-3 flex-shrink-0 flex-wrap text-xs">
            <button
              className={`hover:text-blue-600 ${path.length === 0 ? "font-semibold text-slate-700" : "text-slate-400"}`}
              onClick={() => setPath([])}
            >
              Всё
            </button>
            {path.map((node, i) => (
              <span key={node.id} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3 text-slate-300" />
                <button
                  className={`hover:text-blue-600 ${i === path.length - 1 ? "font-semibold text-slate-700" : "text-slate-400"}`}
                  onClick={() => setPath(path.slice(0, i + 1))}
                >
                  {node.name}
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="px-5 py-4 overflow-y-auto flex flex-col gap-5">
          {isEmpty && (
            <div className="flex flex-col items-center gap-2 py-8 text-slate-400">
              <span className="text-sm">Список пуст</span>
            </div>
          )}

          {groups && filteredGroups.map(g => (
            g.items.length === 0 ? null : (
              <div key={g.key}>
                {g.label && (
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{g.label}</div>
                )}
                <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))" }}>
                  {g.items.map(item => (
                    <ItemTile key={item.id} item={item} selected={selectedIds.includes(item.id)} onToggle={onToggle} onDeleteItem={onDeleteItem} />
                  ))}
                </div>
              </div>
            )
          ))}

          {groupTree && searchResults && (
            searchResults.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-8 text-slate-400">
                <span className="text-sm">Ничего не найдено</span>
              </div>
            ) : (
              <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))" }}>
                {searchResults.map(item => (
                  <ItemTile key={item.id} item={item} selected={selectedIds.includes(item.id)} onToggle={onToggle} onDeleteItem={onDeleteItem} />
                ))}
              </div>
            )
          )}

          {groupTree && !searchResults && (
            <>
              {currentNode.children.length === 0 && currentNode.items.length === 0 && (
                <div className="flex flex-col items-center gap-2 py-8 text-slate-400">
                  <span className="text-sm">Список пуст</span>
                </div>
              )}
              {currentNode.children.length > 0 && (
                <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))" }}>
                  {currentNode.children.map(node => (
                    <div
                      key={node.id}
                      onClick={() => setPath([...path, node])}
                      className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                    >
                      <Folder className="h-6 w-6 text-amber-400" />
                      <span className="text-[11px] font-medium text-center leading-tight text-slate-700 line-clamp-2 w-full">{node.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {currentNode.items.length > 0 && (
                <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))" }}>
                  {currentNode.items.map(item => (
                    <ItemTile key={item.id} item={item} selected={selectedIds.includes(item.id)} onToggle={onToggle} onDeleteItem={onDeleteItem} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end px-5 py-3 border-t border-slate-100 flex-shrink-0">
          <Button size="sm" onClick={onClose}>Готово{selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}</Button>
        </div>
      </div>
    </div>
  );
}
